import * as React from "react";
import mountainsImg from "@/assets/mountains.png";
import cloud3 from "@/assets/cloud-3.png";
import cloud5 from "@/assets/cloud-5.png";
import cloud7 from "@/assets/cloud-7.png";

/**
 * Design assets page — screenshot targets for marketing assets.
 *
 * Renders pixel-exact cards styled with the site's actual brand tokens:
 *   - OG / share preview card (1200 x 630) — og:image, twitter:image
 *   - LinkedIn personal profile banner (1584 x 396)
 *   - LinkedIn company page banner (1128 x 191)
 *
 * To export:
 *   1. Open /_design in Chrome at 100% zoom
 *   2. DevTools (Cmd+Opt+I) → right-click the card → Inspect
 *   3. Command palette (Cmd+Shift+P) → "Capture node screenshot"
 *
 * Dev-only route; not in robots/sitemap, tree-shaken out of prod builds.
 */

// Brand tokens from styles/globals.css — kept in sync manually.
const PAGE_BG = "#f5f2eb";
const SURFACE_2 = "#f0ebe0";
const INK = "#1a1a1a";
const INK_2 = "#5c5c5c";
const ACCENT = "#ef4d23";

export function DesignAssetsPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#1a1a1c",
        display: "flex",
        flexDirection: "column",
        // Left-aligned (not centered) so cards land on whole-pixel x offsets
        // regardless of viewport width — centering can put them on half
        // pixels, which makes node screenshots 1-2px too wide with dark
        // edge slivers from the page background.
        alignItems: "flex-start",
        gap: 16,
        padding: "48px 32px 96px",
        fontFamily: "Inter, sans-serif",
        color: "#e5e5e7",
      }}
    >
      <div
        style={{
          fontSize: 13,
          color: "#a1a1aa",
          maxWidth: 720,
          // Integer line height keeps the cards below on whole-pixel offsets,
          // so node screenshots don't pick up a 1px sliver of page background.
          lineHeight: "21px",
          marginBottom: 24,
        }}
      >
        <strong style={{ color: "#fafafa" }}>Design assets</strong> — screenshot targets at exact
        export size.
        <br />
        DevTools (Cmd+Opt+I) → right-click card → Inspect → command palette (Cmd+Shift+P) → "Capture
        node screenshot".
      </div>

      <SectionLabel
        id="og-card-en"
        label="OG / share preview (EN) — save as public/og-image.png"
        size="1200 × 630"
      />
      <OGCard locale="en" />

      <SectionLabel
        id="og-card-fr"
        label="OG / share preview (FR) — save as public/og-image-fr.png"
        size="1200 × 630"
      />
      <OGCard locale="fr" />

      <SectionLabel
        id="li-profile-banner"
        label="LinkedIn personal profile banner"
        size="1584 × 396"
      />
      <LinkedInProfileBanner />

      <SectionLabel id="li-company-banner" label="LinkedIn company page banner" size="1128 × 191" />
      <LinkedInCompanyBanner />
    </div>
  );
}

function SectionLabel({ id, label, size }: { id: string; label: string; size: string }) {
  return (
    <div
      style={{
        marginTop: 48,
        fontSize: 13,
        lineHeight: "20px",
        color: "#a1a1aa",
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      <span style={{ color: "#fafafa" }}>{label}</span> · {size} ·{" "}
      <code style={{ color: ACCENT }}>#{id}</code>
    </div>
  );
}

function Wordmark({ fontSize }: { fontSize: number }) {
  return (
    <div
      style={{
        fontSize,
        lineHeight: 0.95,
        letterSpacing: "-0.04em",
        fontWeight: 500,
        color: INK,
        whiteSpace: "nowrap",
      }}
    >
      holy
      <span
        style={{
          fontFamily: "Fraunces Variable, Fraunces, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 400,
          color: ACCENT,
          letterSpacing: "-0.02em",
        }}
      >
        moly
      </span>
    </div>
  );
}

function CardShell({
  id,
  width,
  height,
  children,
}: {
  id: string;
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  return (
    <div
      id={id}
      style={{
        width,
        height,
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(180deg, ${PAGE_BG} 0%, ${SURFACE_2} 100%)`,
        fontFamily: "Inter, sans-serif",
        color: INK,
        boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

function Mountains({ height }: { height: number }) {
  return (
    <img
      src={mountainsImg}
      alt=""
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height,
        objectFit: "cover",
        objectPosition: "center bottom",
        pointerEvents: "none",
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* OG / share preview — 1200 × 630, one card per locale                */
/* ------------------------------------------------------------------ */

const OG_MOUNTAIN_H = 230;

interface OGCopy {
  /** "holy<em>moly</em>" — wordmark is the brand, stays English. */
  eyebrow: string;
  taglineLead: string;
  taglineEm: string;
  subTagline: string;
}

const OG_COPY: Record<"en" | "fr", OGCopy> = {
  en: {
    eyebrow: "AI-native development agency",
    taglineLead: "Software with",
    taglineEm: "altitude.",
    subTagline: "Faster. Smarter. AI-native.",
  },
  fr: {
    eyebrow: "Agence de développement nativement IA",
    taglineLead: "Des logiciels avec de la",
    taglineEm: "hauteur.",
    subTagline: "Plus rapides. Plus intelligents. Nativement IA.",
  },
};

function OGCard({ locale }: { locale: "en" | "fr" }) {
  const copy = OG_COPY[locale];
  return (
    <CardShell id={`og-card-${locale}`} width={1200} height={630}>
      {/* Drifting clouds — sky zone only, kept above the mountain band */}
      <img
        src={cloud3}
        alt=""
        style={{
          position: "absolute",
          top: 50,
          left: -120,
          width: 520,
          opacity: 0.5,
          pointerEvents: "none",
        }}
      />
      <img
        src={cloud7}
        alt=""
        style={{
          position: "absolute",
          top: 20,
          right: -160,
          width: 600,
          opacity: 0.45,
          pointerEvents: "none",
        }}
      />
      <img
        src={cloud5}
        alt=""
        style={{
          position: "absolute",
          top: 200,
          right: 320,
          width: 280,
          opacity: 0.4,
          pointerEvents: "none",
        }}
      />

      <Mountains height={OG_MOUNTAIN_H} />

      {/* Sky-zone content (lives above the mountain band) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: OG_MOUNTAIN_H,
          padding: "60px 80px 0px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 16,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: ACCENT,
            fontWeight: 500,
          }}
        >
          {copy.eyebrow}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <Wordmark fontSize={168} />

          <div
            style={{
              fontSize: 40,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              fontWeight: 400,
              color: INK,
              maxWidth: 940,
            }}
          >
            {copy.taglineLead}{" "}
            <span
              style={{
                fontFamily: "Fraunces Variable, Fraunces, Georgia, serif",
                fontStyle: "italic",
                color: ACCENT,
              }}
            >
              {copy.taglineEm}
            </span>
          </div>

          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 17,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: INK_2,
              fontWeight: 500,
              marginTop: 6,
            }}
          >
            {copy.subTagline}
          </div>
        </div>
      </div>
    </CardShell>
  );
}

/* ------------------------------------------------------------------ */
/* Peak-preserving mountain band                                       */
/*                                                                     */
/* mountains.png is 2173 x 393. On wide-short banners an objectFit     */
/* cover crop only shows the foothills, so instead the image is scaled */
/* to the card width at its natural aspect and shifted down — the top  */
/* `visible` px (the peaks) stay inside the card and the base bleeds   */
/* off the bottom edge.                                                */
/* ------------------------------------------------------------------ */

const MOUNTAINS_ASPECT = 393 / 2173;

function PeakMountains({ cardWidth, visible }: { cardWidth: number; visible: number }) {
  const naturalHeight = Math.round(cardWidth * MOUNTAINS_ASPECT);
  return (
    <img
      src={mountainsImg}
      alt=""
      style={{
        position: "absolute",
        bottom: -(naturalHeight - visible),
        left: 0,
        width: "100%",
        height: naturalHeight,
        pointerEvents: "none",
      }}
    />
  );
}

function Tagline({ fontSize }: { fontSize: number }) {
  return (
    <div
      style={{
        fontSize,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
        fontWeight: 400,
        color: INK,
        whiteSpace: "nowrap",
      }}
    >
      Software with{" "}
      <span
        style={{
          fontFamily: "Fraunces Variable, Fraunces, Georgia, serif",
          fontStyle: "italic",
          color: ACCENT,
        }}
      >
        altitude.
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LinkedIn personal profile banner — 1584 × 396                       */
/*                                                                     */
/* The profile photo overlaps the bottom-left of the banner, so the    */
/* lockup sits on the right; internally left-aligned so eyebrow,       */
/* wordmark and tagline share one clean left edge.                     */
/* ------------------------------------------------------------------ */

function LinkedInProfileBanner() {
  return (
    <CardShell id="li-profile-banner" width={1584} height={396}>
      <img
        src={cloud3}
        alt=""
        style={{
          position: "absolute",
          top: 18,
          left: 40,
          width: 360,
          opacity: 0.45,
          pointerEvents: "none",
        }}
      />
      <img
        src={cloud5}
        alt=""
        style={{
          position: "absolute",
          top: 96,
          left: 470,
          width: 210,
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />
      <img
        src={cloud7}
        alt=""
        style={{
          position: "absolute",
          top: -30,
          right: -150,
          width: 460,
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />

      <PeakMountains cardWidth={1584} visible={210} />

      {/* Lockup, right of the profile-photo safe area */}
      <div
        style={{
          position: "absolute",
          top: 40,
          right: 96,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 14,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: ACCENT,
            fontWeight: 500,
            marginBottom: 20,
            marginLeft: 6,
          }}
        >
          AI-native development agency
        </div>

        <Wordmark fontSize={104} />

        <div style={{ marginTop: 14, marginLeft: 6 }}>
          <Tagline fontSize={27} />
        </div>
      </div>
    </CardShell>
  );
}

/* ------------------------------------------------------------------ */
/* LinkedIn company page banner — 1128 × 191                           */
/*                                                                     */
/* The company logo tile overlaps the bottom-left of the cover, so the */
/* lockup is one baseline-aligned row, centered in the sky zone.       */
/* ------------------------------------------------------------------ */

function LinkedInCompanyBanner() {
  return (
    <CardShell id="li-company-banner" width={1128} height={191}>
      <img
        src={cloud7}
        alt=""
        style={{
          position: "absolute",
          top: -24,
          right: -90,
          width: 320,
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />
      <img
        src={cloud5}
        alt=""
        style={{
          position: "absolute",
          top: 8,
          left: 30,
          width: 190,
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />

      {/* Distant ridge: mirrored, faded copy filling the empty left sky.
          Rendered before the main band so the foreground overlaps it. */}
      <img
        src={mountainsImg}
        alt=""
        style={{
          position: "absolute",
          bottom: -36,
          left: -160,
          width: 1000,
          height: Math.round(1000 * MOUNTAINS_ASPECT),
          transform: "scaleX(-1)",
          opacity: 0.45,
          pointerEvents: "none",
        }}
      />

      <PeakMountains cardWidth={1128} visible={100} />

      {/* Lockup on the right: wordmark with tagline underneath */}
      <div
        style={{
          position: "absolute",
          top: 28,
          right: 80,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          zIndex: 2,
        }}
      >
        <Wordmark fontSize={58} />

        <div style={{ marginTop: 10, marginLeft: 4 }}>
          <Tagline fontSize={22} />
        </div>
      </div>
    </CardShell>
  );
}
